import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';

interface PrivateRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  requireSuperAdmin?: boolean;
  requireWhiteLabel?: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, requiredRoles, requireSuperAdmin, requireWhiteLabel }) => {
  const { isAuthenticated, isInitializing, shouldResetPassword, user, currentOrganization, token } = useAuth();
  const location = useLocation();

  if (isInitializing) {
    return null;
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (shouldResetPassword && location.pathname !== '/reset-password') {
    // Passar o token de autenticação como query parameter
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
    return <Navigate to={`/reset-password${tokenParam}`} replace />;
  }

  // Verificar se o usuário precisa selecionar um plano
  // Permitir acesso apenas à rota /change-plan se não tiver plano
  const needsPlanSelection = !currentOrganization?.currentPlan || localStorage.getItem('needsPlanSelection') === 'true';
  if (needsPlanSelection && location.pathname !== '/change-plan' && location.pathname !== '/payment') {
    return <Navigate to="/change-plan" replace state={{ forceSelection: true }} />;
  }

  // Check Super Admin permission
  if (requireSuperAdmin && (!user || !user.isSuperAdmin)) {
    return <Navigate to="/admin" replace />;
  }

  // Check WhiteLabel permission
  if (requireWhiteLabel && (!user || !user.isWhiteLabel)) {
    return <Navigate to="/admin" replace />;
  }

  // Check role permissions
  if (requiredRoles && requiredRoles.length > 0 && user) {
    const hasRequiredRole = requiredRoles.includes(user.role);
    if (!hasRequiredRole) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

export default PrivateRoute;